import { TrailerManifestUs,HazmatControllerUs } from "@routemax/hazmat";
import placardInfo from "../../../../Settings/Terminal/placards/jsonFiles/placardInfo.json";
import segregationRules from "../../../../Settings/Terminal/placards/jsonFiles/segregationRules.json";
import segregationRuleWeights from "../../../../Settings/Terminal/placards/jsonFiles/segregationRuleWeights.json";

const HAZMAT_PACKING_STATUS = { FORBIDDEN: "forbidden" };

export class HazmatUtilityClass {
  static hazmatController;
  static mappedHazmatInfo;

  //Map the hazmatInfo from API to smallcase fields
  static getHazmatInfo = (hazmatInfo) => {
    if (!HazmatUtilityClass.mappedHazmatInfo) {
      HazmatUtilityClass.mappedHazmatInfo =
        hazmatInfo.length &&
        hazmatInfo.map((value) => ({
          unid: value.unid,
          packagegroup: value.packageGroup,
          keyseq: value.keySeq,
          class: value.class,
          specialprovision: value.specialProvision,
          description: value.description,
          noshipflag: value.noShipFlag,
          inhzone: value.inhZone,
        }));
    }
    return HazmatUtilityClass.mappedHazmatInfo;
  };
  static getHazmatController = (hazmatInfo) => {
    if (!HazmatUtilityClass.hazmatController) {
      HazmatUtilityClass.hazmatController = new HazmatControllerUs({
        hazmatInfo,
        placardInfo,
        segregationRules,
        segregationRuleWeights,
      });
    }
    return HazmatUtilityClass.hazmatController;
  };

  static isAnagram = (str1, str2) => {
    const normalize = (str) =>
      str
        .toLowerCase()
        .replace(/[^a-z0-9]/gi, "")
        .split("")
        .sort()
        .join("");
    return normalize(str1) === normalize(str2);
  };

  //get unique restrictions based on description
  static removeDuplicateRestriction = (hazmatRestrictions) => {
    const uniqueDescription = [
      ...new Set(hazmatRestrictions.map((item) => item.description)),
    ];
    return uniqueDescription.reduce((acc, current) => {
      const restriction = acc?.find((desc) =>
        HazmatUtilityClass.isAnagram(desc, current)
      );
      return !restriction ? acc?.concat([current]) : acc;
    }, []);
  };

  static filterHazmatRestrictionType = (hazmatPlacardsInfo) => {
    if (hazmatPlacardsInfo.packingStatus === HAZMAT_PACKING_STATUS.FORBIDDEN) {
      return hazmatPlacardsInfo?.restrictions?.filter(
        (res) => res.type === "banned"
      );
    } else {
      return hazmatPlacardsInfo?.restrictions;
    }
  };

  static getHazmatWithUpdatedRestrictions = (hazmatPlacardsInfo) => {
    let hazmatWithUpdatedRestrictions = hazmatPlacardsInfo;
    let filteredRestrictions =
      HazmatUtilityClass.filterHazmatRestrictionType(hazmatPlacardsInfo);
    if (filteredRestrictions?.length) {
      const restrictions =
        HazmatUtilityClass.removeDuplicateRestriction(filteredRestrictions);
      hazmatWithUpdatedRestrictions = {
        ...hazmatPlacardsInfo,
        restrictions: restrictions,
      };
    }
    return hazmatWithUpdatedRestrictions;
  };

  static constructShipmentHazmatData = (
    shipmentData,
    hazmatInfo,
    hazmatClass
  ) => {
    let shipmentHazmatData = {};
    shipmentHazmatData = {
      ...shipmentData,
      ...hazmatInfo,
      class: hazmatClass ? hazmatClass : hazmatInfo.class,
      pieces: parseInt(hazmatInfo.pieces),
      weight: parseInt(hazmatInfo.weight),
    };
    return shipmentHazmatData;
  };

  static getMultipleUNPGCombinationData = (shipmentData, hazmatInfo,hazmatPlacardsInfo) => {
    let shipmentHazmatData = [];
    let validClasses = [];
    const hazmatInfoFromAPIResponse = hazmatPlacardsInfo || [];
    let multipleUNCombination = hazmatInfoFromAPIResponse.filter(
      //If class is not present in any case(e.g. Pickup) then find a matching class from hazmatInfo from API response and use it
      (placard) =>
        placard.packageGroup === hazmatInfo.pgNumber &&
        placard.unid === hazmatInfo.unNumber
    );

    // This is subject to change, we are taking only first class as safety team not sure about how to handle this.
    if (multipleUNCombination.length > 0) {
      validClasses.push(
        multipleUNCombination.sort((class1, class2) =>
          class1.keySeq.localeCompare(class2.keySeq)
        )[0]
      );
    }

    let constructedData = {};
    validClasses.forEach((hazmat) => {
      constructedData = HazmatUtilityClass.constructShipmentHazmatData(
        shipmentData,
        hazmatInfo,
        hazmat.class
      );
      shipmentHazmatData.push(constructedData);
    });
    return shipmentHazmatData;
  };

  static transformData = (shipments,hazmatPlacardInfo) => {
    let transformedShipmentData = [];
    // hazmatInfo from API response to pass to the hazmatController
    const hazmatInfoFromAPIResponse = hazmatPlacardInfo || [];

    const allShipmentsInTrailer = [...shipments];
    // data structure for creating shipmentData and hazmatData to pass to TailerManifest.factory
    allShipmentsInTrailer.forEach((shipment) => {
      if (shipment.proNumber || shipment.proId) {
        let constructedData = [];
        let shipmentHazmatData = {
          proNumber: shipment.proNumber || shipment.proId,
          isFood: shipment.isFood,
        };
        //push hazmatInfo in transformedShipmentData if hazmatInfo is present for any shipment, otherwise push {proNumber, isFood}
        shipment?.hazmatInfo?.length
          ? shipment.hazmatInfo.forEach((data) => {
              if (!data.class) {
                constructedData =
                  HazmatUtilityClass.getMultipleUNPGCombinationData(
                    shipmentHazmatData,
                    data,
                    hazmatPlacardInfo
                  );
                transformedShipmentData.push(...constructedData);
              } else {
                constructedData =
                  HazmatUtilityClass.constructShipmentHazmatData(
                    shipmentHazmatData,
                    data
                  );
                transformedShipmentData.push(constructedData);
              }
            })
          : transformedShipmentData.push(shipmentHazmatData);
      }
    });

    //shipmentData to be passed to the trailerManifestUs
    const allShipmentDataForManifest = transformedShipmentData.map((data) => {
      return {
        proNumber: data.proNumber,
        isFood: data.isFood ? data.isFood : false,
      };
    });

    //hazmatData to be passed to the trailerManifestUs
    let hazmatDataForManifest = [];
    transformedShipmentData
      .filter((data) => data.unNumber)
      .forEach((data) => {
        if (data.class && data.unNumber && data.weight) {
          const hazmat = {
            proNumber: data?.proNumber,
            unNumber: data?.unNumber,
            pgNumber: data?.pgNumber,
            allowedToTransport: Boolean(data?.allowedToTransport),
            class: data?.class,
            bulk: data?.bulk,
            pieces: data?.pieces || 0,
            packageCode: data?.packageCode || "",
            weight: data?.weight,
          };
          hazmatDataForManifest.push(hazmat);
        }
      });

    if (hazmatDataForManifest?.length && hazmatInfoFromAPIResponse?.length) {
      const mappedHazmatInfoForController = HazmatUtilityClass.getHazmatInfo(
        hazmatInfoFromAPIResponse
      );
      const hazmatController = HazmatUtilityClass.getHazmatController(
        mappedHazmatInfoForController
      );
      const manifest = TrailerManifestUs.factory(
        // Shipment data of ALL shipments in the trailer, including non-hazmat shipments
        allShipmentDataForManifest,

        // Extended hazmat data of all hazmat shipments
        hazmatDataForManifest
      );

      const result = hazmatController.analyze(manifest);
      result.placards.forEach((placard) => {
        if (placard.hazmatUnCodes && placard.hazmatUnCodes.length > 0)
          placard.hazmatUnCodes = placard.hazmatUnCodes[0];
      });
      return HazmatUtilityClass.getHazmatWithUpdatedRestrictions(result);

    }
    return {};
  };
}

export const getHazmatPlacards = (shipments, hazmatPlacardInfo) => {
  const hazmatInfo = HazmatUtilityClass.transformData(shipments,hazmatPlacardInfo);
  return hazmatInfo.placards;
};
